import { BaseInput, Icon, SpaceBetween, tk } from '@sunwebgroup/ui-components';
import { css, styled } from 'styled-components';
import type { ChangeEventHandler } from 'react';
import * as React from 'react';
import type { FilterOption } from '@/scopes/search/types';
import type { DestinationMetadata } from '@/scopes/search/quick-search/domain/types';
import { Destination } from '@/scopes/search/quick-search/components/DestinationFilter/DestinationOption';
import { useIntl } from '@/intl';

interface FilterProps {
  options: FilterOption<string, DestinationMetadata>[];
  onItemClick: (selection: string) => void;
}

export function Root({ options, onItemClick }: FilterProps) {
  const intl = useIntl();
  const [inputValue, setInputValue] = React.useState('');
  const [isFocused, setIsFocused] = React.useState(false);

  const onChange: ChangeEventHandler = (e) => {
    const { value } = e.target as HTMLInputElement;
    setInputValue(value);
  };

  const destinationOptions = React.useMemo(() => {
    return options
      .filter(
        (option: FilterOption<string, DestinationMetadata>) =>
          option.name.toLowerCase().startsWith(inputValue.toLowerCase()) ||
          option.metadata?.parentName?.toLowerCase().startsWith(inputValue.toLowerCase())
      )
      .map((option: FilterOption<string, DestinationMetadata>) => (
        <Destination option={option} onItemClick={onItemClick} key={option.value} query={inputValue} />
      ));
  }, [inputValue, options, onItemClick]);

  return (
    <Content direction="y" spacing={tk('spacing_sm')}>
      <BaseInput.Root>
        <StyledBox $focused={isFocused}>
          <Icon glyph="IconSearch" />
          <StyledContent
            as="input"
            onChange={onChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            type="text"
            placeholder={intl.formatMessage({ id: 'quicksearch_filter_destination_search_placeholder' })}
            value={inputValue}
          />
        </StyledBox>
      </BaseInput.Root>
      {destinationOptions}
    </Content>
  );
}

const Content = styled(SpaceBetween)`
  @media ${tk('mq_mobile')} {
    padding: ${tk('spacing_md')};
  }
  @media ${tk('mq_desktop_md')} {
    max-width: 340px;
    padding: ${tk('spacing_lg')} ${tk('spacing_lg')} 0;
  }
`;

export const Footer = styled.div`
  background-color: ${tk('color_semantic_bg')};
  bottom: 0;
  display: flex;
  padding: ${tk('spacing_xs')} ${tk('spacing_lg')} ${tk('spacing_lg')};
  position: -webkit-sticky;
  position: sticky;
  width: 100%;
  z-index: 1;
`;

const StyledBox = styled(BaseInput.Box)<{ $focused: boolean }>`
  padding: ${tk('spacing_xs')} ${tk('spacing_sm')};
  ${({ $focused }) =>
    $focused &&
    css`
      box-shadow: ${tk('shadow_input_focus_grey')};
    `}
`;
const StyledContent = styled(BaseInput.Content)`
  padding: 0 0 0 ${tk('spacing_xs')};
  border: none;
  color: ${tk('color_semantic_text_placeholder')};
  &:focus {
    border: none;
    outline: none;
    box-shadow: none;
  }
`;
